import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { FileText, Users, Car, BarChart3, Percent, Settings, Shield } from 'lucide-react';
import { isAuthenticated, getLicenseStatus } from '../services/auth';

const Features = () => {
  const { darkMode } = useContext(ThemeContext);
  const [hideTrialBanner, setHideTrialBanner] = useState(isAuthenticated());

  useEffect(() => {
    const checkLicenseStatus = async () => {
      if (isAuthenticated()) {
        try {
          const status = await getLicenseStatus();
          setHideTrialBanner(status.trialLicense || status.hasPaidLicense);
        } catch (error) {
          console.error('Error checking license status:', error);
          setHideTrialBanner(false);
        }
      } else {
        setHideTrialBanner(false);
      }
    };
    checkLicenseStatus();
  }, []);

  const coreFeatures = [
    {
      icon: FileText,
      color: 'text-blue-500',
      title: 'Invoice Creation',
      description: 'Create, customize, and print professional invoices from one screen.',
      details: [
        'Custom line items with configurable item types (Part, Labor, Non-Taxable, and your own)',
        'Automatic tax calculation per item type with custom tax rates',
        'Auto-incrementing estimate numbers',
        'Service writer and service tech fields',
        'Insurance claim number support',
        'Unlimited invoice creation'
      ]
    },
    {
      icon: Users,
      color: 'text-green-500',
      title: 'Customer Database',
      description: 'Store and manage all your customer records in one place.',
      details: [
        'Store unlimited customer records (name, phone, email, address)',
        'Quick search by customer name',
        'Auto-populate customer info on invoices',
        'View past invoices by customer in Reports',
        'Create new customers directly from the invoice screen'
      ]
    },
    {
      icon: Car,
      color: 'text-purple-500',
      title: 'Vehicle Database',
      description: 'Look up any vehicle by year, make, and model with detailed specs. Includes FREE yearly updates.',
      details: [
        'Comprehensive database with year, make, model, and trim',
        'Vehicle specs: engine displacement, cylinders, drive type, fuel type, size class',
        'Hierarchical filtering (Year \u2192 Make \u2192 Model)',
        'Manual VIN entry for unlisted vehicles',
        'Mileage tracking per invoice',
        'FREE automatic yearly database updates'
      ]
    },
    {
      icon: BarChart3,
      color: 'text-orange-500',
      title: 'Reports & Filtering',
      description: 'View, filter, and print your invoice history with powerful search tools.',
      details: [
        'Filter by estimate number, date range, service tech, customer, or claim number',
        'Quick filters: Today, Yesterday, This Week, This Month',
        'Paginated results with sortable columns',
        'Print individual invoices from report view',
        'Export reports to PDF',
        'View invoice totals for filtered results'
      ]
    },
    {
      icon: Percent,
      color: 'text-red-500',
      title: 'Flexible Discounts',
      description: 'Apply discounts at the item level or across the entire invoice.',
      details: [
        'Per-item discounts (flat dollar amount or percentage)',
        'Invoice-level global discounts',
        'Create reusable discount templates',
        'Discount protection (cannot reduce below zero)',
        'Both discount types can be enabled or disabled independently'
      ]
    },
    {
      icon: Settings,
      color: 'text-teal-500',
      title: 'Customizable Settings',
      description: 'Tailor the software to match your business needs.',
      details: [
        'Custom tax rates per line item type',
        'Company logo, name, address, and contact info',
        'Custom invoice disclaimer text',
        'Create and manage your own line item types',
        'Printer selection and configuration',
        'Database backup and restore (CSV export/import)'
      ]
    }
  ];

  return (
    <div className={`flex-1 flex flex-col ${darkMode ? 'dark' : ''} bg-white dark:bg-gray-900`}>
      <div className="flex-grow">

        {/* Header */}
        <div className="text-center pt-16 pb-10 px-4">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white tracking-tight mb-4">
            Everything your shop needs.<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 dark:from-blue-400 dark:to-cyan-300">Nothing it doesn't.</span>
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            Built for auto repair shops and independent mechanics. Every feature is designed around how you actually work.
          </p>
        </div>

        {/* Trial Banner */}
        {!hideTrialBanner && (
          <div className="max-w-3xl mx-auto mb-12 px-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-green-900 dark:text-green-300">Try free for 7 days</p>
                <p className="text-sm text-green-700 dark:text-green-400">Full access to all features. No credit card required.</p>
              </div>
              <Link to="/register"
                className="inline-flex items-center px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors whitespace-nowrap">
                Start Free Trial
              </Link>
            </div>
          </div>
        )}

        {/* All Features */}
        <div className="max-w-6xl mx-auto px-4 pb-20">
          <div className="grid md:grid-cols-2 gap-6">
            {coreFeatures.map((feature, index) => (
              <div key={index} className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <feature.icon className={`w-6 h-6 ${feature.color} flex-shrink-0`} />
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">{feature.title}</h2>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start">
                      <svg className="w-4 h-4 text-blue-500 mr-2.5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-300 text-sm">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Data Security — full width */}
            <div className="md:col-span-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-lg p-6">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Data Security</h3>
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Your data is encrypted and stored securely on your machine. Only you have access to it.
              </p>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default Features;
